import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { 
  Zap, Crown, TrendingUp, Sparkles, 
  ArrowUpRight, AlertCircle, CheckCircle2, Plus, Calendar
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const PLAN_ICONS = {
  payg: Zap,
  trial: Zap,
  free: Zap,
  starter: TrendingUp,
  professional: Crown,
  business: Sparkles,
  pilot: Crown
};

const PLAN_COLORS = {
  payg: "text-yellow-500",
  trial: "text-blue-400",
  free: "text-blue-400",
  starter: "text-blue-500",
  professional: "text-primary",
  business: "text-purple-500",
  pilot: "text-green-500"
};

const PLAN_NAMES = {
  payg: "Pay As You Go",
  trial: "Trial",
  free: "Free Trial",
  starter: "Starter",
  professional: "Professional",
  business: "Business",
  pilot: "Pilot (Unlimited)"
};

const UsageCard = ({ API }) => {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [borrowing, setBorrowing] = useState(false);

  useEffect(() => {
    fetchQuotaStatus();
  }, []);

  const fetchQuotaStatus = async () => {
    try {
      const response = await fetch(`${API}/quota/status`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setQuota(data);
      }
    } catch (error) {
      console.error("Failed to fetch quota status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBorrow = async () => {
    setBorrowing(true);
    try {
      const response = await fetch(`${API}/quota/borrow`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchQuotaStatus(); // Refresh quota status
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to borrow quotes");
      }
    } catch (error) {
      toast.error("Failed to borrow quotes");
    } finally {
      setBorrowing(false);
    }
  };

  const handleBuyCredits = async (credits = 5) => {
    try {
      const response = await fetch(`${API}/quota/buy-payg`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          credits,
          success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/dashboard`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Failed to buy credits");
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-white/5">
        <CardContent className="p-6">
          <div className="h-24 bg-secondary/50 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!quota) {
    return null;
  }

  const PlanIcon = PLAN_ICONS[quota.plan] || Zap;
  const planColor = PLAN_COLORS[quota.plan] || "text-primary";
  const planName = PLAN_NAMES[quota.plan] || quota.plan;
  
  const isUnlimited = quota.is_unlimited;
  const isPAYG = quota.plan === "payg";
  const isTrial = quota.plan === "trial" || quota.plan === "free";
  
  const quotesUsed = quota.quotes_used || 0;
  const quotaLimit = quota.quota_limit || 0;
  const quotesRemaining = quota.quotes_remaining || 0;
  const paygCredits = quota.payg_credits || 0;
  const borrowedFromNext = quota.borrowed_from_next || 0;
  const nextMonthAvailable = quota.next_month_available || 0;
  
  // Calculate usage percentage
  const usagePercentage = quotaLimit > 0 
    ? Math.min(100, Math.round((quotesUsed / quotaLimit) * 100))
    : 0;
  
  // Determine status
  const isLow = !isUnlimited && quotesRemaining <= 5 && quotesRemaining > 0;
  const isEmpty = !isUnlimited && quotesRemaining === 0 && paygCredits === 0;
  const canBorrow = nextMonthAvailable > 0 && !isPAYG && !isTrial;

  return (
    <Card className="bg-card border-white/5" data-testid="usage-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <PlanIcon className={`w-5 h-5 ${planColor}`} />
            Your Plan
          </span>
          <Badge 
            variant="outline" 
            className={`${planColor} border-current`}
          >
            {planName}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isUnlimited ? (
          // Unlimited (Pilot) Display
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-500 text-sm bg-green-500/10 p-3 rounded">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Unlimited quotes available</span>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Quotes generated</span>
              <span className="font-bold text-foreground mono">{quotesUsed}</span>
            </div>
          </div>
        ) : isPAYG ? (
          // PAYG Display
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Credits available</span>
              <span className="font-bold text-xl mono">{paygCredits}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Price per quote</span>
              <span className="font-medium">£2.00</span>
            </div>
            {paygCredits === 0 && (
              <div className="flex items-center gap-2 text-yellow-500 text-sm bg-yellow-500/10 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                <span>Add credits to generate quotes</span>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Button 
                size="sm"
                className="flex-1"
                onClick={() => handleBuyCredits(5)}
                data-testid="buy-5-credits-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Buy 5 (£10)
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/pricing")}
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Subscribe
              </Button>
            </div>
          </div>
        ) : (
          // Subscription Display
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Monthly usage</span>
              <span className="font-medium">
                {quotesUsed} / {quotaLimit} quotes
              </span>
            </div>
            
            <Progress 
              value={usagePercentage} 
              className="h-2"
            />
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Remaining</span>
              <span className={`font-bold text-lg mono ${isEmpty ? 'text-red-500' : isLow ? 'text-yellow-500' : 'text-green-500'}`}>
                {quotesRemaining}
                {paygCredits > 0 && <span className="text-sm text-muted-foreground ml-1">(+{paygCredits} PAYG)</span>}
              </span>
            </div>

            {borrowedFromNext > 0 && (
              <div className="flex items-center gap-2 text-blue-400 text-xs bg-blue-500/10 p-2 rounded">
                <Calendar className="w-3 h-3" />
                <span>Borrowed {borrowedFromNext} from next month</span>
              </div>
            )}

            {isEmpty && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                <span>No credits left</span>
              </div>
            )}

            {isLow && !isEmpty && (
              <div className="flex items-center gap-2 text-yellow-500 text-sm bg-yellow-500/10 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                <span>Running low on credits!</span>
              </div>
            )}

            {!isEmpty && !isLow && (
              <div className="flex items-center gap-2 text-green-500 text-sm bg-green-500/10 p-2 rounded">
                <CheckCircle2 className="w-4 h-4" />
                <span>You're all set this month</span>
              </div>
            )}

            <div className="flex gap-2 mt-2">
              {canBorrow && quotesRemaining <= 5 && (
                <Button 
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={handleBorrow}
                  disabled={borrowing}
                  data-testid="borrow-btn"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  {borrowing ? "..." : "Borrow 5"}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm"
                    variant={isEmpty ? "default" : "outline"}
                    className="flex-1"
                    data-testid="buy-credits-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Buy Credits
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  <DropdownMenuItem onClick={() => handleBuyCredits(1)} data-testid="buy-1-credit">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                    1 Credit - £9
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBuyCredits(3)} data-testid="buy-3-credits">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                    3 Credits - £27
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBuyCredits(5)} data-testid="buy-5-credits">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                    5 Credits - £45
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBuyCredits(10)} data-testid="buy-10-credits">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                    10 Credits - £90
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {quota.plan !== "business" && (
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate("/pricing")}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageCard;
